// src/features/tower/storeReadiness/StoreReadinessPage.tsx

import React, { useState, useEffect, useCallback } from "react";
import type { StoreReadinessPayload, SessionStage, ReadinessTile as TileType } from "./types";
import { getStoreReadiness } from "./api";
import { todaySAST } from "./time";
import { BranchStageSelector } from "./components/BranchStageSelector";
import { StatusLegend } from "./components/StatusLegend";
import { EmployeeCarousel } from "./components/EmployeeCarousel";
import { ReadinessGroup } from "./components/ReadinessGroup";
import { TaskDrawer } from "./components/TaskDrawer";
import { ObservationDrawer } from "./components/ObservationDrawer";

type PayloadWithAccess = StoreReadinessPayload & {
  allowedBranchIds?: string[] | null;
};

// -----------------------------------------------------------------------
// Read allowed branch IDs from the login token so buttons are correct
// from the very first frame, before the server responds.
// -----------------------------------------------------------------------
function getInitialAllowedBranches(): string[] | null {
  try {
    const raw = localStorage.getItem("gotrue.user");
    if (!raw) return null;
    const user = JSON.parse(raw);
    const token: string | undefined = user?.token?.access_token;
    if (!token) return null;
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    const roles: unknown[] = payload?.app_metadata?.roles ?? [];
    if (!Array.isArray(roles)) return null;
    const branchIds: string[] = [];
    for (const r of roles) {
      const m = String(r).match(/^branch([A-Za-z]{3})$/);
      if (m) branchIds.push(m[1].toUpperCase());
    }
    return branchIds.length > 0 ? branchIds : null;
  } catch {
    return null;
  }
}

// -----------------------------------------------------------------------
// Pick the current shift stage from the SAST clock:
//   before 14:00 → OPENING
//   14:00–19:59  → CHANGEOVER
//   20:00+       → CLOSING
// -----------------------------------------------------------------------
function currentStageSAST(): SessionStage {
  const now = new Date();
  const sast = new Date(now.toLocaleString("en-US", { timeZone: "Africa/Johannesburg" }));
  const hour = sast.getHours();
  if (hour < 14) return "OPENING";
  if (hour < 20) return "CHANGEOVER";
  return "CLOSING";
}

export function StoreReadinessPage() {
  const [branchId, setBranchId] = useState("");
  const [stage, setStage] = useState<SessionStage>(() => currentStageSAST());
  const date = todaySAST();
  const [data, setData] = useState<StoreReadinessPayload | null>(null);
  const [allowedBranchIds, setAllowedBranchIds] = useState<string[] | null>(
    () => getInitialAllowedBranches()
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTile, setSelectedTile] = useState<TileType | null>(null);
  const [showObservationDrawer, setShowObservationDrawer] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = (await getStoreReadiness({ branch: branchId, date, stage })) as PayloadWithAccess;
      setData(result);
      setAllowedBranchIds(result.allowedBranchIds ?? null);
    } catch (e: any) {
      setError(e?.message || "Could not load Store Readiness data.");
    } finally {
      setLoading(false);
    }
  }, [branchId, date, stage]);

  useEffect(() => {
    load();
  }, [load]);

  const effectiveBranchId = branchId || (data ? data.branch.branchId : "");

  const handleDrawerDone = () => {
    setSelectedTile(null);
    load();
  };

  return (
    <div>
      <div
        style={{
          padding: "10px 14px",
          borderRadius: 8,
          background: "var(--ct-accent-bg)",
          color: "var(--ct-accent)",
          fontSize: 12.5,
          fontWeight: 600,
          marginBottom: 16,
        }}
      >
        Attendance discipline accounts for 20% of an employee's overall performance score.
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <BranchStageSelector
          branchId={effectiveBranchId}
          stage={stage}
          onBranchChange={setBranchId}
          onStageChange={setStage}
          allowedBranchIds={allowedBranchIds}
        />
        {data && (
          <button
            onClick={() => setShowObservationDrawer(true)}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: "1px solid var(--ct-line)",
              background: "var(--ct-surface)",
              color: "var(--ct-ink-2)",
              fontFamily: "inherit",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Log observation
          </button>
        )}
      </div>

      {loading && (
        <div style={{ padding: "40px 0", textAlign: "center", color: "var(--ct-ink-3)", fontSize: 13 }}>
          Loading…
        </div>
      )}

      {!loading && error && (
        <div
          style={{
            padding: "16px",
            borderRadius: 8,
            background: "#FEE2E2",
            color: "#991B1B",
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          {error}
          <button
            onClick={load}
            style={{
              marginLeft: 12,
              fontFamily: "inherit",
              fontSize: 12,
              fontWeight: 600,
              padding: "4px 10px",
              borderRadius: 6,
              border: "1px solid #991B1B",
              background: "transparent",
              color: "#991B1B",
              cursor: "pointer",
            }}
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && data && (
        <>
          <StatusLegend />
          <EmployeeCarousel
            employees={data.employees}
            permissions={data.permissions}
            onRecalculated={load}
          />

          {data.taskGroups.length === 0 ? (
            <div style={{ fontSize: 13, color: "var(--ct-ink-3)", padding: "20px 0" }}>
              No tasks configured for this branch and stage yet.
            </div>
          ) : (
            data.taskGroups.map((group) => (
              <ReadinessGroup key={group.category} group={group} onTileClick={setSelectedTile} />
            ))
          )}

          <div style={{ fontSize: 11, color: "var(--ct-ink-3)", marginTop: 20 }}>
            Last refreshed: {new Date(data.lastRefreshed).toLocaleTimeString()}
          </div>

          {selectedTile && (
            <TaskDrawer
              tile={selectedTile}
              branchId={effectiveBranchId}
              date={date}
              stage={stage}
              employees={data.employees}
              permissions={data.permissions}
              onClose={() => setSelectedTile(null)}
              onDone={handleDrawerDone}
            />
          )}

          {showObservationDrawer && (
            <ObservationDrawer
              branchId={effectiveBranchId}
              sessionId={data.session.sessionId}
              employees={data.employees}
              sopDefinitions={data.sopDefinitions}
              permissions={data.permissions}
              onClose={() => setShowObservationDrawer(false)}
              onDone={load}
            />
          )}
        </>
      )}
    </div>
  );
}
